import type { ComponentProps } from 'react';
import type {
  PlayEvent,
  PlayEventMatch,
  PlayEventParticipant,
  PlayEventTeam,
  PlayerRatingSettings,
} from '@court-sync/shared';
import {
  canScorePlayEventMatch,
  canUpdatePlayEventMatchScore,
  getPlayEventParticipantDisplayName,
  getPopulatedPlayEventMatchSide,
  resolvePlayEventMatchFormatLabel,
  resolvePlayEventMatchSideCapacity,
} from '@court-sync/shared';
import type { PlayEventScoreInput } from '@/services/playEvent';
import { PlayEventMatchScoreForm } from '@/components/play/PlayEventMatchScoreForm';
import { cn } from '@/lib/utils';
import { listAvailablePlayEventCourtsForRound } from '@court-sync/shared';
import {
  Badge,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui';
import {
  PlayEventDrawPlayerSlot,
  playEventDrawDropParticipantId,
} from '@/components/play/PlayEventDrawPlayerSlot';
import {
  PlayEventDrawEmptySlot,
  type DrawEmptySlotParticipantOption,
} from '@/components/play/PlayEventDrawEmptySlot';
import { PlayEventDrawUnevenPlayersPanel } from '@/components/play/PlayEventDrawUnevenPlayersPanel';
import { parseEmptySlotDropId } from '@/components/play/PlayEventDrawEmptySlot';
import type { DragEndEvent } from '@dnd-kit/core';
import { User, Users } from 'lucide-react';

export interface DrawMatchListItem {
  id: string;
  roundNumber: number;
  matchIndex: number;
  sideAIds: string[];
  sideBIds: string[];
  sideATeamId?: string | null;
  sideBTeamId?: string | null;
  courtId?: string | null;
  result?: PlayEventMatch['result'] | 'scheduled';
  scoreLabel?: string;
  winnerSide?: 'a' | 'b' | null;
  score?: PlayEventMatch['score'];
  isBye?: boolean;
  matchLabel?: string;
  sideAPlaceholder?: string;
  sideBPlaceholder?: string;
  sideAIsFeeder?: boolean;
  sideBIsFeeder?: boolean;
}

interface PlayEventDrawMatchListProps {
  event: PlayEvent;
  organizationId: string;
  eventId: string;
  matches: DrawMatchListItem[];
  participantsById: Map<string, PlayEventParticipant>;
  teamsById?: Map<string, PlayEventTeam>;
  courtNamesById: Record<string, string>;
  ratingSettings: PlayerRatingSettings | null;
  editable: boolean;
  draggingParticipantId?: string | null;
  sittingOutParticipants?: PlayEventParticipant[];
  unevenPanelTitle?: string;
  onParticipantUpdated?: () => void;
  onCourtChange?: (matchId: string, courtId: string | null) => void;
  onRemoveParticipant?: (participantId: string, matchId: string) => void;
  assigningCourtMatchId?: string | null;
  onDragEnd?: (event: DragEndEvent) => void;
  availableParticipantsForSlots?: DrawEmptySlotParticipantOption[];
  onAssignParticipantToSlot?: (
    participantId: string,
    matchKey: string,
    side: 'a' | 'b',
    slotIndex?: number
  ) => void;
  onRecordScore?: (matchId: string, data: PlayEventScoreInput) => void;
  scoringMatchId?: string | null;
  /** When true, assigned slots awaiting roster resolution show a loader instead of TBD. */
  participantsLoading?: boolean;
}

function isSideAwaitingParticipants(
  participantIds: string[],
  participantsById: Map<string, PlayEventParticipant>,
  participantsLoading: boolean
): boolean {
  if (!participantsLoading || participantIds.length === 0) {
    return false;
  }
  return participantIds.some((participantId) => !participantsById.has(participantId));
}

function PlayEventDrawMatchSideLoading() {
  return (
    <div
      className="flex min-h-[2.75rem] items-center gap-2 rounded-md bg-muted/30 px-2 py-2 text-sm text-muted-foreground"
      aria-busy="true"
      aria-label="Loading player"
    >
      <div
        className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-muted-foreground"
        aria-hidden
      />
      <span>Loading…</span>
    </div>
  );
}

function resolveDrawMatchSideParticipants(
  sideIds: string[],
  teamId: string | null | undefined,
  participantsById: Map<string, PlayEventParticipant>,
  teamsById: Map<string, PlayEventTeam>
): PlayEventParticipant[] {
  if (teamId) {
    const team = teamsById.get(teamId);
    if (team) {
      return [team.participantAId, team.participantBId]
        .map((participantId) => participantsById.get(participantId))
        .filter((participant): participant is PlayEventParticipant => Boolean(participant));
    }
  }

  if (sideIds.length > 0) {
    return sideIds
      .map((participantId) => participantsById.get(participantId))
      .filter((participant): participant is PlayEventParticipant => Boolean(participant));
  }

  return [];
}

function formatDrawMatchSideLabel(participants: PlayEventParticipant[]): string {
  if (participants.length === 0) return 'TBD';
  return participants.map((participant) => getPlayEventParticipantDisplayName(participant)).join(' & ');
}

function getMatchStatusLabel(match: DrawMatchListItem): string {
  if (match.result === 'unplayed' || match.result === 'scheduled' || !match.result) {
    return 'Scheduled';
  }
  if (match.result === 'bye') return 'Bye';
  if (match.result === 'winner') return 'Completed';
  if (match.result === 'walkover') return 'Walkover';
  if (match.result === 'retired') return 'Retired';
  if (match.result === 'cancelled') return 'Cancelled';
  return match.result;
}

function renderDrawMatchSide({
  side,
  teamLabel,
  sideParticipants,
  sideCapacity,
  isDoublesMatch,
  match,
  placeholder,
  showFeeder,
  isLoading,
  editable,
  draggingParticipantId,
  availableParticipantsForSlots,
  onAssignParticipantToSlot,
  onRemoveParticipant,
  playerSlotProps,
}: {
  side: 'a' | 'b';
  teamLabel: string;
  sideParticipants: PlayEventParticipant[];
  sideCapacity: number;
  isDoublesMatch: boolean;
  match: DrawMatchListItem;
  placeholder?: string;
  showFeeder: boolean;
  isLoading: boolean;
  editable: boolean;
  draggingParticipantId: string | null;
  availableParticipantsForSlots: DrawEmptySlotParticipantOption[];
  onAssignParticipantToSlot?: PlayEventDrawMatchListProps['onAssignParticipantToSlot'];
  onRemoveParticipant?: PlayEventDrawMatchListProps['onRemoveParticipant'];
  playerSlotProps: Omit<
    ComponentProps<typeof PlayEventDrawPlayerSlot>,
    'participant' | 'showSinglesPreference'
  >;
}) {
  return (
    <>
      {isDoublesMatch ? (
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {teamLabel}
        </p>
      ) : null}
      {showFeeder ? (
        <p className="rounded-md border border-dashed border-muted-foreground/30 px-2 py-2 text-sm italic text-muted-foreground">
          {placeholder}
        </p>
      ) : null}
      {isLoading ? <PlayEventDrawMatchSideLoading /> : null}
      {!showFeeder && !isLoading ? (
        <div className={cn('space-y-1', isDoublesMatch && 'rounded-lg bg-background/70 p-2')}>
          {Array.from({ length: sideCapacity }, (_, slotIndex) => {
            const participant = sideParticipants[slotIndex];
            return (
              <div key={`${match.id}-${side}-slot-${slotIndex}`}>
                {isDoublesMatch && slotIndex > 0 ? (
                  <p className="py-1 text-center text-[11px] font-medium text-muted-foreground">
                    &
                  </p>
                ) : null}
                {participant ? (
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <PlayEventDrawPlayerSlot
                        participant={participant}
                        {...playerSlotProps}
                        showSinglesPreference={participant.prefersSingles === true}
                      />
                    </div>
                    {editable && onRemoveParticipant ? (
                      <button
                        type="button"
                        className="mt-1 text-xs text-muted-foreground hover:text-foreground"
                        onClick={() => onRemoveParticipant(participant.id, match.id)}
                      >
                        Remove
                      </button>
                    ) : null}
                  </div>
                ) : (
                  <PlayEventDrawEmptySlot
                    matchKey={match.id}
                    side={side}
                    slotIndex={slotIndex}
                    editable={editable}
                    label="Add player"
                    draggingParticipantId={draggingParticipantId}
                    availableParticipants={availableParticipantsForSlots}
                    onAssignParticipant={
                      onAssignParticipantToSlot
                        ? (participantId) =>
                            onAssignParticipantToSlot(participantId, match.id, side, slotIndex)
                        : undefined
                    }
                  />
                )}
              </div>
            );
          })}
        </div>
      ) : null}
    </>
  );
}

export function playEventDrawMatchListDragHandler(
  dragEvent: DragEndEvent,
  handlers: {
    onSwap: (sourceParticipantId: string, targetParticipantId: string) => void;
    onAssignEmpty: (
      sourceParticipantId: string,
      matchKey: string,
      side: 'a' | 'b',
      slotIndex?: number
    ) => void;
  }
): void {
  const sourceId = String(dragEvent.active.id);
  if (!dragEvent.over) return;

  const emptySlot = parseEmptySlotDropId(dragEvent.over.id);
  if (emptySlot) {
    handlers.onAssignEmpty(sourceId, emptySlot.matchKey, emptySlot.side, emptySlot.slotIndex);
    return;
  }

  const targetId = playEventDrawDropParticipantId(dragEvent.over.id);
  if (!targetId || sourceId === targetId) return;
  handlers.onSwap(sourceId, targetId);
}

export function PlayEventDrawMatchList({
  event,
  organizationId,
  eventId,
  matches,
  participantsById,
  teamsById = new Map(),
  courtNamesById,
  ratingSettings,
  editable,
  draggingParticipantId = null,
  sittingOutParticipants = [],
  unevenPanelTitle = 'Sitting out',
  onParticipantUpdated,
  onCourtChange,
  onRemoveParticipant,
  assigningCourtMatchId = null,
  availableParticipantsForSlots = [],
  onAssignParticipantToSlot,
  onRecordScore,
  scoringMatchId = null,
  participantsLoading = false,
}: PlayEventDrawMatchListProps) {
  const eventCourtOptions = event.courtIds.map((courtId) => ({
    id: courtId,
    name: courtNamesById[courtId] ?? 'Unknown court',
  }));

  const playerSlotProps = {
    event,
    organizationId,
    eventId,
    ratingSettings,
    onUpdated: onParticipantUpdated,
    editable,
    draggingParticipantId,
  };

  return (
    <div className="space-y-4">
      <ul className="space-y-3">
        {matches.map((match) => {
          const courtName = match.courtId ? courtNamesById[match.courtId] : null;
          const selectedCourtLabel =
            match.courtId != null
              ? (eventCourtOptions.find((court) => court.id === match.courtId)?.name ?? 'Court TBD')
              : 'Court TBD';
          const availableCourtIds = listAvailablePlayEventCourtsForRound(
            event.courtIds,
            matches.map((entry) => ({
              id: entry.id,
              courtId: entry.courtId ?? null,
            })),
            match.id
          );

          const sideA = resolveDrawMatchSideParticipants(
            match.sideAIds,
            match.sideATeamId,
            participantsById,
            teamsById
          );
          const sideB = resolveDrawMatchSideParticipants(
            match.sideBIds,
            match.sideBTeamId,
            participantsById,
            teamsById
          );

          const legacyMatch = {
            id: match.id,
            sideAParticipantId: sideA[0]?.id ?? match.sideAIds[0] ?? null,
            sideBParticipantId: sideB[0]?.id ?? match.sideBIds[0] ?? null,
            sideATeamId: match.sideATeamId ?? null,
            sideBTeamId: match.sideBTeamId ?? null,
            result: match.result === 'scheduled' ? 'unplayed' : (match.result ?? 'unplayed'),
          } as PlayEventMatch;

          const sideCapacity = resolvePlayEventMatchSideCapacity(event);
          const matchFormatLabel = resolvePlayEventMatchFormatLabel(event);
          const isDoublesMatch = sideCapacity >= 2;

          const canScore = onRecordScore != null && canScorePlayEventMatch(legacyMatch);
          const canUpdateScore =
            onRecordScore != null && canUpdatePlayEventMatchScore(legacyMatch);
          const showScoreForm =
            event.matchScoringEnabled && (canScore || canUpdateScore);
          const singlePopulatedSide =
            legacyMatch.result === 'unplayed' || canUpdateScore
              ? getPopulatedPlayEventMatchSide(legacyMatch)
              : null;
          const showSideAFeeder =
            Boolean(match.sideAIsFeeder && match.sideAPlaceholder) && match.result !== 'bye';
          const showSideBFeeder =
            Boolean(match.sideBIsFeeder && match.sideBPlaceholder) && match.result !== 'bye';
          const sideALoading = isSideAwaitingParticipants(
            match.sideAIds,
            participantsById,
            participantsLoading
          );
          const sideBLoading = isSideAwaitingParticipants(
            match.sideBIds,
            participantsById,
            participantsLoading
          );
          const sideALabel =
            sideA.length > 0
              ? formatDrawMatchSideLabel(sideA)
              : sideALoading
                ? 'Loading…'
                : (match.sideAPlaceholder ?? 'Team A');
          const sideBLabel =
            sideB.length > 0
              ? formatDrawMatchSideLabel(sideB)
              : sideBLoading
                ? 'Loading…'
                : (match.sideBPlaceholder ?? 'Team B');

          return (
            <li
              key={match.id}
              className="rounded-xl border border-border bg-card px-4 py-4 shadow-sm"
            >
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  {match.matchLabel ? (
                    <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {match.matchLabel}
                    </span>
                  ) : null}
                {editable && onCourtChange && eventCourtOptions.length > 0 && match.result !== 'bye' ? (
                  <Select
                    value={match.courtId ?? 'unassigned'}
                    onValueChange={(value) => {
                      onCourtChange(match.id, value === 'unassigned' ? null : value);
                    }}
                    disabled={assigningCourtMatchId === match.id}
                  >
                    <SelectTrigger
                      className="h-8 w-[min(100%,14rem)] text-sm font-medium"
                      aria-label={`Court for match ${match.id}`}
                    >
                      <SelectValue placeholder="Assign court">{selectedCourtLabel}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Court TBD</SelectItem>
                      {eventCourtOptions.map((court) => (
                        <SelectItem
                          key={court.id}
                          value={court.id}
                          disabled={
                            match.courtId !== court.id && !availableCourtIds.includes(court.id)
                          }
                        >
                          {court.name}
                          {match.courtId !== court.id && !availableCourtIds.includes(court.id)
                            ? ' (in use)'
                            : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-sm font-medium text-foreground">{courtName ?? 'Court TBD'}</p>
                )}
                  <Badge
                    variant="secondary"
                    className="gap-1 font-normal text-muted-foreground"
                  >
                    {isDoublesMatch ? (
                      <Users className="h-3 w-3" aria-hidden />
                    ) : (
                      <User className="h-3 w-3" aria-hidden />
                    )}
                    {matchFormatLabel}
                  </Badge>
                </div>
                <span className="text-xs text-muted-foreground">
                  {getMatchStatusLabel(match)}
                </span>
              </div>

              {match.score?.sets?.length ? (
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <span className="text-xs font-medium text-muted-foreground">Sets</span>
                  {match.score.sets.map((set, setIndex) => (
                    <span
                      key={`${match.id}-set-${setIndex}`}
                      className="inline-flex items-center gap-1 rounded-md border border-border bg-muted/40 px-2 py-1 text-xs font-medium tabular-nums"
                    >
                      <span className="text-muted-foreground">S{setIndex + 1}</span>
                      <span>{set.a}</span>
                      <span className="text-muted-foreground">-</span>
                      <span>{set.b}</span>
                    </span>
                  ))}
                </div>
              ) : null}

              {match.result === 'bye' || match.isBye ? (
                <p className="font-medium">Bye</p>
              ) : (
                <div className="grid gap-4 md:grid-cols-[1fr_auto_1fr] md:items-stretch">
                  <div
                    className={cn(
                      'rounded-xl border p-3',
                      match.winnerSide === 'a'
                        ? 'border-emerald-500/40 bg-emerald-50/80 dark:bg-emerald-500/10'
                        : 'border-border/70 bg-muted/20'
                    )}
                  >
                    {renderDrawMatchSide({
                      side: 'a',
                      teamLabel: 'Team A',
                      sideParticipants: sideA,
                      sideCapacity,
                      isDoublesMatch,
                      match,
                      placeholder: match.sideAPlaceholder,
                      showFeeder: showSideAFeeder,
                      isLoading: sideALoading,
                      editable,
                      draggingParticipantId,
                      availableParticipantsForSlots,
                      onAssignParticipantToSlot,
                      onRemoveParticipant,
                      playerSlotProps,
                    })}
                  </div>
                  <div className="flex items-center justify-center self-center px-1">
                    <span
                      className={cn(
                        'inline-flex min-h-8 min-w-8 items-center justify-center rounded-full border border-border bg-background text-xs font-semibold uppercase tracking-wide text-muted-foreground',
                        isDoublesMatch && 'md:min-h-10 md:min-w-10'
                      )}
                    >
                      vs
                    </span>
                  </div>
                  <div
                    className={cn(
                      'rounded-xl border p-3',
                      match.winnerSide === 'b'
                        ? 'border-emerald-500/40 bg-emerald-50/80 dark:bg-emerald-500/10'
                        : 'border-border/70 bg-muted/20'
                    )}
                  >
                    {renderDrawMatchSide({
                      side: 'b',
                      teamLabel: 'Team B',
                      sideParticipants: sideB,
                      sideCapacity,
                      isDoublesMatch,
                      match,
                      placeholder: match.sideBPlaceholder,
                      showFeeder: showSideBFeeder,
                      isLoading: sideBLoading,
                      editable,
                      draggingParticipantId,
                      availableParticipantsForSlots,
                      onAssignParticipantToSlot,
                      onRemoveParticipant,
                      playerSlotProps,
                    })}
                  </div>
                </div>
              )}

              {showScoreForm ? (
                <PlayEventMatchScoreForm
                  key={`${match.id}:${match.winnerSide ?? ''}:${JSON.stringify(match.score?.sets ?? [])}`}
                  sideALabel={sideALabel}
                  sideBLabel={sideBLabel}
                  mode={canUpdateScore ? 'update' : 'record'}
                  singlePopulatedSide={singlePopulatedSide}
                  initialValues={{
                    winnerSide: match.winnerSide ?? null,
                    sets: match.score?.sets,
                  }}
                  isLoading={scoringMatchId === match.id}
                  onSubmit={(data) => onRecordScore?.(match.id, data)}
                />
              ) : null}
            </li>
          );
        })}
      </ul>

      {sittingOutParticipants.length > 0 ? (
        <PlayEventDrawUnevenPlayersPanel
          title={unevenPanelTitle}
          participants={sittingOutParticipants}
          {...playerSlotProps}
        />
      ) : null}
    </div>
  );
}
